import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { getApiErrorMessage } from '../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext(null);

const GUEST_CART_KEY = 'lemo_guest_cart';

const getGuestCart = () => {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const saveGuestCart = (cart) => {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
};

const clearGuestCartStorage = () => {
  localStorage.removeItem(GUEST_CART_KEY);
};

const createEmptyCart = () => ({ items: [], subtotal: 0, total_amount: 0, service_fee: 0, restaurant: null, restaurant_id: null });

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, isClient } = useAuth();
  const [cart, setCart] = useState(createEmptyCart());
  const [loading, setLoading] = useState(false);

  // Recalculate guest cart subtotal
  const recalcGuestCart = (cartData) => {
    const subtotal = cartData.items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const serviceFee = cartData.service_fee || 0;
    return { ...cartData, subtotal: Math.round(subtotal * 100) / 100, total_amount: Math.round((subtotal + serviceFee) * 100) / 100 };
  };

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated || !isClient) {
      // Non-authenticated or non-client users: use guest cart from localStorage
      if (!isAuthenticated) {
        const guestCart = getGuestCart();
        if (guestCart) {
          setCart(recalcGuestCart(guestCart));
        } else {
          setCart(createEmptyCart());
        }
      } else {
        // Authenticated non-client (Restaurant/Admin): empty cart
        setCart(createEmptyCart());
      }
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch {
      console.error('Error fetching cart');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isClient]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // When user logs in as CLIENT, migrate guest cart to server if it exists
  useEffect(() => {
    if (isAuthenticated && isClient) {
      const guestCart = getGuestCart();
      if (guestCart && guestCart.items.length > 0) {
        (async () => {
          try {
            for (const item of guestCart.items) {
              await axios.post(`${API}/cart/add`, {
                dish_id: item.dish_id,
                quantity: item.quantity,
                selected_options: item.selected_options,
                selected_ingredients: item.selected_ingredients,
                special_instructions: item.special_instructions
              });
            }
            clearGuestCartStorage();
            fetchCart();
          } catch {
            clearGuestCartStorage();
          }
        })();
      }
    }
  }, [isAuthenticated, isClient, fetchCart]);

  const addToCart = async (dishId, quantity = 1, selectedOptions = null, specialInstructions = null, selectedIngredients = null, dishData = null) => {
    if (isAuthenticated && isClient) {
      try {
        await axios.post(`${API}/cart/add`, {
          dish_id: dishId,
          quantity,
          selected_options: selectedOptions,
          selected_ingredients: selectedIngredients,
          special_instructions: specialInstructions
        });
        await fetchCart();
        return true;
      } catch (err) {
        const detail = getApiErrorMessage(err, "Erreur lors de l'ajout au panier");
        if (typeof detail === 'string' && detail.includes('Videz-le avant')) {
          const error = new Error(detail);
          error.isCartTypeMismatch = true;
          throw error;
        }
        throw detail;
      }
    }

    // Guest mode: store in localStorage
    if (!dishData) {
      throw new Error('Données du plat manquantes pour le mode invité');
    }

    const guestCart = getGuestCart() || createEmptyCart();
    
    // Check restaurant mismatch
    if (guestCart.restaurant_id && guestCart.restaurant_id !== dishData.restaurant_id) {
      const error = new Error('Votre panier contient des plats d\'un autre restaurant. Videz-le avant de commander ici.');
      error.isCartTypeMismatch = true;
      throw error;
    }

    // Calculate unit price with options
    let unitPrice = dishData.price || 0;
    if (selectedOptions && dishData.options) {
      dishData.options.forEach(opt => {
        const sel = selectedOptions[opt.id];
        if (sel) {
          opt.choices?.forEach(ch => {
            if (opt.type === 'multiple_choice') {
              if (Array.isArray(sel) && sel.includes(ch.id)) unitPrice += ch.price || ch.price_delta || 0;
            } else {
              if (sel === ch.id) unitPrice += ch.price || ch.price_delta || 0;
            }
          });
        }
      });
    }

    // Calculate ingredient supplement prices
    if (selectedIngredients && dishData.ingredients?.enabled && Array.isArray(dishData.ingredients?.categories)) {
      let ingredientTotal = dishData.ingredients.base_price || 0;
      dishData.ingredients.categories.forEach(category => {
        const categorySelection = selectedIngredients[category?.id] || [];
        const freeQuantity = Number(category?.free_quantity) || 0;
        let selectionCount = 0;
        const items = Array.isArray(category?.items) ? category.items : [];
        items.forEach(item => {
          if (categorySelection.includes(item?.id)) {
            const itemPrice = Number(item?.price) || 0;
            if (itemPrice < 0) {
              ingredientTotal += itemPrice;
            } else {
              selectionCount++;
              if (selectionCount > freeQuantity && itemPrice > 0) {
                ingredientTotal += itemPrice;
              }
            }
          }
        });
      });
      unitPrice += ingredientTotal;
    }

    const itemId = `guest-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newItem = {
      id: itemId,
      dish_id: dishId,
      dish_name: dishData.name,
      dish_image: dishData.image || null,
      quantity,
      unit_price: unitPrice,
      selected_options: selectedOptions,
      selected_ingredients: selectedIngredients,
      special_instructions: specialInstructions
    };

    guestCart.items.push(newItem);
    guestCart.restaurant_id = dishData.restaurant_id;
    guestCart.restaurant = dishData.restaurant || guestCart.restaurant;

    const updated = recalcGuestCart(guestCart);
    saveGuestCart(updated);
    setCart(updated);
    return true;
  };

  const clearAndAddToCart = async (dishId, quantity = 1, selectedOptions = null, specialInstructions = null, selectedIngredients = null, dishData = null) => {
    if (isAuthenticated && isClient) {
      await axios.delete(`${API}/cart`);
      await axios.post(`${API}/cart/add`, {
        dish_id: dishId, quantity, selected_options: selectedOptions,
        selected_ingredients: selectedIngredients, special_instructions: specialInstructions
      });
      await fetchCart();
      return true;
    }

    // Guest: clear and add
    const guestCart = createEmptyCart();
    clearGuestCartStorage();
    saveGuestCart(guestCart);
    setCart(guestCart);
    return addToCart(dishId, quantity, selectedOptions, specialInstructions, selectedIngredients, dishData);
  };

  const updateCartItem = async (itemId, quantity) => {
    if (isAuthenticated && isClient) {
      try {
        await axios.put(`${API}/cart/item/${itemId}`, { quantity });
        await fetchCart();
      } catch (err) {
        throw getApiErrorMessage(err, 'Erreur lors de la mise à jour');
      }
      return;
    }

    // Guest mode
    const guestCart = getGuestCart();
    if (!guestCart) return;
    const item = guestCart.items.find(i => i.id === itemId);
    if (item) {
      item.quantity = quantity;
      const updated = recalcGuestCart(guestCart);
      saveGuestCart(updated);
      setCart(updated);
    }
  };

  const removeFromCart = async (itemId) => {
    if (isAuthenticated && isClient) {
      try {
        await axios.delete(`${API}/cart/item/${itemId}`);
        await fetchCart();
      } catch (err) {
        throw getApiErrorMessage(err, 'Erreur lors de la suppression');
      }
      return;
    }

    // Guest mode
    const guestCart = getGuestCart();
    if (!guestCart) return;
    guestCart.items = guestCart.items.filter(i => i.id !== itemId);
    if (guestCart.items.length === 0) {
      clearGuestCartStorage();
      setCart(createEmptyCart());
    } else {
      const updated = recalcGuestCart(guestCart);
      saveGuestCart(updated);
      setCart(updated);
    }
  };

  const clearCart = async () => {
    if (isAuthenticated && isClient) {
      try {
        await axios.delete(`${API}/cart`);
        setCart(createEmptyCart());
      } catch (err) {
        throw getApiErrorMessage(err, 'Erreur lors du vidage du panier');
      }
      return;
    }

    clearGuestCartStorage();
    setCart(createEmptyCart());
  };

  const itemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const isPreorderCart = cart.is_preorder_cart || false;
  const preorderDelayDays = cart.preorder_delay_days || 0;
  const isGuestCart = !isAuthenticated && cart.items?.length > 0;

  const value = {
    cart,
    loading,
    addToCart,
    clearAndAddToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart,
    itemCount,
    isPreorderCart,
    preorderDelayDays,
    isGuestCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
